"use client";

import type { ReactNode } from "react";

import type { AppIcon } from "@/components/appIcons";
import type { LinkTarget } from "@/components/link";
import { cn } from "@/lib/utils";

import { FacetOptionRow } from "./facet-option-row";

export interface FacetOption {
  value: string;
  label: ReactNode;
  count?: number | null;
  disabled?: boolean;
  icon?: AppIcon;
  link?: LinkTarget;
}

interface FacetOptionListProps {
  items: FacetOption[];
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  formatCount: (count: number) => string;
}

interface FacetOptionGridProps extends FacetOptionListProps {
  className?: string;
  prominent?: boolean;
}

export function FacetOptionRows({ items, value, onChange, formatCount }: FacetOptionListProps) {
  const maxCount = getMaxCount(items);

  return (
    <div className="grid gap-1.5">
      {items.map((item) => (
        <FacetOptionRow
          key={item.value}
          item={item}
          value={value}
          maxCount={maxCount}
          onChange={onChange}
          formatCount={formatCount}
        />
      ))}
    </div>
  );
}

export function FacetOptionGrid({
  items,
  value,
  onChange,
  formatCount,
  className,
  prominent = false,
}: FacetOptionGridProps) {
  return (
    <div className={cn("grid grid-cols-2 gap-1.5", className)}>
      {items.map((item) => {
        const isActive = value === item.value;
        const disabled = item.disabled && !isActive;
        const Icon = item.icon;

        return (
          <button
            key={item.value}
            type="button"
            disabled={disabled}
            aria-pressed={isActive}
            onClick={() => onChange(isActive ? undefined : item.value)}
            className={cn(
              "min-w-0 rounded-full border transition-colors",
              "flex items-center justify-between gap-1.5 text-left",
              prominent ? "min-h-11 px-4 py-2.5" : "min-h-11 px-2.5 py-1.5 sm:min-h-9",
              isActive
                ? "border-primary/60 bg-primary/10 text-primary"
                : disabled
                  ? "border-border/50 bg-secondary/30 text-muted-foreground/40 cursor-not-allowed"
                  : "border-border bg-secondary/50 text-muted-foreground hover:border-border/80 hover:text-foreground",
            )}
          >
            <span className="flex min-w-0 items-center gap-2">
              {Icon ? (
                <Icon className={cn("shrink-0", prominent ? "text-lg" : "text-base")} />
              ) : null}
              <span
                className={cn(
                  "min-w-0 truncate font-semibold",
                  prominent ? "text-sm" : "text-[13px]",
                )}
              >
                {item.label}
              </span>
            </span>
            {item.count != null && item.count > 0 && (
              <span className="shrink-0 text-[11px] font-semibold tabular-nums opacity-60">
                {formatCount(item.count)}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function getMaxCount(items: FacetOption[]) {
  return Math.max(0, ...items.map((item) => item.count ?? 0));
}
